import { createReducer, on } from '@ngrx/store'
import { AppMessageValue, ChatOutbox, OutboxEntry, OutboxStatus } from '../app.state'
import {
  chatMessageFailed,
  chatMessageSent,
  chatMessageWaiting,
  chatOutboxCleared,
  chatOutboxRestored,
  queueChatMessage,
  removeOutboxMessage,
  retryChatMessage,
} from '../actions/chat-outbox.actions'
import { newChatMessage, updateMessages } from '../actions/corp-member.actions'
import { chatSendErrorAutoRetries } from '../../utils/chat-outbox.util'

function without(outbox: ChatOutbox, clientMsgIds: Array<string | null | undefined>): ChatOutbox {
  const gone = clientMsgIds.filter((id): id is string => !!id && id in outbox)
  if (!gone.length) return outbox
  const next = { ...outbox }
  gone.forEach((id) => delete next[id])
  return next
}

function withStatus(outbox: ChatOutbox, clientMsgId: string, status: OutboxStatus, changes: Partial<OutboxEntry> = {}): ChatOutbox {
  const entry = outbox[clientMsgId]
  if (!entry) return outbox
  return {
    ...outbox,
    [clientMsgId]: { ...entry, ...changes, status, message: { ...entry.message, send_status: status } },
  }
}

export const chatOutboxReducer = createReducer<ChatOutbox>(
  {},
  on(queueChatMessage, (outbox, { entry }) => ({ ...outbox, [entry.outgoing.client_msg_id]: entry })),
  on(retryChatMessage, (outbox, { client_msg_id }) => withStatus(outbox, client_msg_id, 'sending', { error: undefined })),
  on(chatMessageWaiting, (outbox, { client_msg_id }) => withStatus(outbox, client_msg_id, 'waiting')),
  on(chatMessageFailed, (outbox, { client_msg_id, error }) =>
    withStatus(outbox, client_msg_id, 'failed', { error, autoRetry: chatSendErrorAutoRetries(error) })),
  on(chatMessageSent, (outbox, { client_msg_id }) => without(outbox, [client_msg_id])),
  // Its saved copy can also turn up without this send's ack: in the room broadcast (before the
  // ack, or when the ack was lost), or in a refetch of the chats.
  on(newChatMessage, (outbox, chat) => without(outbox, [chat.client_msg_id])),
  on(updateMessages, (outbox, action) => {
    if (!Object.keys(outbox).length) return outbox
    const rooms = Object.values(action as unknown as Record<string, Partial<AppMessageValue> | string>)
    const savedIds = rooms.flatMap((room) => (typeof room === 'object' ? room?.texts ?? [] : []).map((t) => t.client_msg_id))
    return without(outbox, savedIds)
  }),
  on(removeOutboxMessage, (outbox, { client_msg_id }) => without(outbox, [client_msg_id])),
  // Idempotent: one already in the store (still being handled) is kept as it is.
  on(chatOutboxRestored, (outbox, { entries }) =>
    entries.reduce((next, entry) => (entry.outgoing.client_msg_id in next ? next : { ...next, [entry.outgoing.client_msg_id]: entry }), outbox)),
  on(chatOutboxCleared, () => ({})),
)
